import React from "/vendor/react";
import gql from "/vendor/graphql-tag";

import { PollingDuration } from "../../constant";
import { ApolloError } from "/vendor/apollo-client";
import { isUnreachable } from "/lib/util/fetchError";
import {
  useApolloClient,
  useQuery,
  useRouter,
  UseQueryResult,
} from "/lib/component/util";
import { parseStringParam } from "/lib/util/params";
import {
  AppLayout,
  NotFound,
  HandlerConfigDialog,
  HandlerDetailsContainer,
} from "/lib/component/partial";
import { useManageResourceStatusToast } from "/lib/component/toast";

import deleteHandler from "/lib/mutation/deleteHandler";

export const handlerDetailsViewFragments = {
  record: gql`
    fragment HandlerDetailsView_record on Handler {
      id
      deleted @client
      ...HandlerDetailsContainer_handler
    }

    ${HandlerDetailsContainer.fragments.handler}
  `,
};

const handlerDetailsViewQuery = gql`
  query HandlerDetailsContentQuery($namespace: String!, $handler: String!) {
    handler(namespace: $namespace, name: $handler) {
      id
      ...HandlerDetailsView_record
    }
  }

  ${handlerDetailsViewFragments.record}
`;

interface Variables {
  handler: string;
  namespace: string;
}

interface HandlerDetailsViewContentProps {
  toolbarItems?: (items: any[]) => React.ReactNode;
  query: UseQueryResult<any, any>;
  variables: Variables;
  onClickEdit: () => void;
  onClickDelete: () => void;
}

export function useHandlerDetailsViewQueryVariables(): Variables {
  const router = useRouter();
  const params = router.match.params as any;

  return {
    namespace: parseStringParam(params.namespace, ""),
    handler: parseStringParam(params.handler, ""),
  };
}

export const HandlerDetailsViewContent = ({
  query,
  toolbarItems,
  onClickEdit,
  onClickDelete,
}: HandlerDetailsViewContentProps) => {
  const { aborted, data = {}, networkStatus } = query;
  const { handler } = data;
  // see: https://github.com/apollographql/apollo-client/blob/master/packages/apollo-client/src/core/networkStatus.ts
  const loading = networkStatus < 6;

  if (!loading && !aborted && (!handler || handler.deleted)) {
    return (
      <AppLayout>
        <NotFound />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <HandlerDetailsContainer
        toolbarItems={toolbarItems}
        handler={handler}
        loading={loading || aborted}
        onClickEdit={onClickEdit}
        onClickDelete={onClickDelete}
      />
    </AppLayout>
  );
};

export const HandlerDetailsView = () => {
  const client = useApolloClient();
  const { history } = useRouter();
  const createManageResourceStatusToast = useManageResourceStatusToast();
  const [editing, setEditing] = React.useState(false);

  const variables = useHandlerDetailsViewQueryVariables();
  const query = useQuery({
    query: handlerDetailsViewQuery,
    pollInterval: PollingDuration.short,
    fetchPolicy: "cache-and-network",
    variables,
    onError: (error: Error): void => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  const onClickDelete = React.useCallback(() => {
    const handler = (query.data || {}).handler;
    if (!handler) {
      return;
    }

    createManageResourceStatusToast(deleteHandler(client, { id: handler.id }), {
      resourceType: "handler",
      action: "delete",
      resourceName: variables.handler,
    });

    // optimistically redirect back to the handlers list
    history.push(history.location.pathname.replace(/\/[^/.]+$/, ""));
  }, [
    client,
    query.data,
    variables.handler,
    history,
    createManageResourceStatusToast,
  ]);

  return (
    <React.Fragment>
      <HandlerDetailsViewContent
        query={query}
        variables={variables}
        onClickEdit={() => setEditing(true)}
        onClickDelete={onClickDelete}
      />
      {editing && (
        <HandlerConfigDialog
          namespace={variables.namespace}
          handlerName={variables.handler}
          onClose={() => setEditing(false)}
          onSuccess={() => query.refetch()}
        />
      )}
    </React.Fragment>
  );
};
